import * as React from 'react';
import {
  PartLabel,
  WorkspaceButton,
  WorkspaceInput,
  WorkspaceSelect,
} from '@/components/ui/workspace';
import { formatDay, plural } from '@/components/discovery/discovery-logic';
import {
  requestMapChanges,
  validateMap,
  type MapAnswer,
  type MapValidationPayload,
} from '@/lib/api/secure-links';
import { cn } from '@/lib/utils/cn';
import { ClientCard } from './client-frame';
import { bpmnId, parseBpmnId } from '@caliber-2/discovery-core/maps';
import { MapCanvas } from '@/components/discovery/maps/map-canvas';
import {
  composeStepComments,
  midSentence,
  nameAndTitle,
  namesMatch,
  type StepComment,
} from './secure-link-logic';
import { DoneCard, problemText, type LinkPageProps } from './secure-link-parts';

/**
 * The client's map validation page (E12-F09 §3.3; prototype
 * `#client-map/<process>`): only this version of the current-state map,
 * read only, the bpmn.io watermark visible and nothing laid over the
 * diagram. The validator picks a step and comments on it; comments are
 * listed beside the map. "This is how we work" validates, once the typed
 * name matches the person the link was sent to; "Send my comments" asks
 * for changes instead. The comments go as one, a "Step name: comment" line
 * per step (the T-0182 contract).
 */

/** The map routes store one comment of at most this many characters. */
const MAX_COMMENT = 4_000;

export function ClientMapValidation({
  token,
  session,
  payload,
  onInvalid,
  onAnswered,
}: LinkPageProps & { payload: MapValidationPayload; onAnswered?: () => void }) {
  const [comments, setComments] = React.useState<StepComment[]>([]);
  const [step, setStep] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState('');
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState<'validate' | 'send' | null>(null);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [done, setDone] = React.useState<(MapAnswer & { comments: number }) | null>(null);

  const who = payload.validator;
  const first = session.person_first_name || who.full_name.split(/\s+/)[0] || '';
  const process = midSentence(payload.title);
  const steps = React.useMemo(
    () => payload.steps.filter((s) => s.type !== 'start_event' && s.type !== 'end_event'),
    [payload.steps],
  );
  const stepName = React.useCallback(
    (key: string) => payload.steps.find((s) => s.key === key)?.name ?? key,
    [payload.steps],
  );
  const pickable = React.useMemo(() => new Set(steps.map((s) => s.key)), [steps]);
  // Marks are classes on the shapes (the shared map canvas, T-0189): dashed
  // when nobody described the step, outlined when picked. Nothing else.
  const markers = React.useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const s of payload.steps) {
      const cls: string[] = [];
      if (s.confirmation === 'unconfirmed') cls.push('mk-unconf');
      if (s.key === step) cls.push('mk-sel');
      if (cls.length) out[bpmnId('step', s.key)] = cls;
    }
    return out;
  }, [payload.steps, step]);

  // Already answered from this link (a reload, or a second visit).
  const answered = done ?? (payload.answer ? { ...payload.answer, comments: -1 } : null);
  if (answered) {
    const n = answered.comments;
    const received =
      n > 0
        ? `Your ${plural(n, 'comment')} ${n === 1 ? 'has' : 'have'} been received`
        : 'Your comments have been received';
    return (
      <DoneCard
        title={first ? `Thank you, ${first}` : 'Thank you'}
        meta={
          answered.outcome === 'validated'
            ? `Validated by ${who.full_name}, ${formatDay(answered.answered_at)} · version ${payload.version_no}`
            : undefined
        }
      >
        {answered.outcome === 'validated'
          ? `You confirmed that this is how ${process} works today.${n > 0 ? ` ${received} too.` : ''}`
          : `${received}. The map stays a draft until it is right.`}
      </DoneCard>
    );
  }

  const nameOk = namesMatch(name, who.full_name);
  const composed = composeStepComments(comments, stepName);
  const tooLong = composed.length > MAX_COMMENT;

  const addComment = () => {
    const text = draft.trim();
    if (!step || !text) return;
    setComments((prev) => [...prev, { step, text }]);
    setDraft('');
    setStep(null);
  };

  const finish = async (kind: 'validate' | 'send') => {
    if (tooLong) return;
    setBusy(kind);
    setProblem(null);
    try {
      const res =
        kind === 'validate'
          ? await validateMap(token, {
              typed_name: name,
              ...(composed ? { comment: composed } : {}),
            })
          : await requestMapChanges(token, composed);
      setDone({ ...res, comments: comments.length });
      onAnswered?.();
    } catch (err) {
      if (!onInvalid(err)) setProblem(problemText(err, 'Your answer could not be sent.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <ClientCard>
        <div className="text-meta text-ink-500">
          {session.client_name} · for {nameAndTitle(who.full_name, who.title)}
        </div>
        <h1 className="my-1 text-h1 font-semibold text-ink-900">
          Is this how {process} works today?
        </h1>
        <p className="text-body text-ink-700">
          This map was drawn from what your colleagues told us. Pick any step to comment on it. When
          it is right, say so below; if something is wrong, send your comments instead.
          {payload.unconfirmed_steps > 0
            ? ' Dashed steps are ones nobody has described yet: please check those especially.'
            : ''}
        </p>
      </ClientCard>

      <ClientCard className="!p-4">
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="-mx-4 -my-2 flex h-[516px] flex-col">
            <MapCanvas
              xml={payload.bpmn_xml}
              markers={markers}
              editable={false}
              label={`Map of ${process}, version ${payload.version_no}`}
              onSelect={(id) => {
                const ref = parseBpmnId(id);
                const key = ref?.kind === 'step' ? ref.key : null;
                // Steps and decisions only, as on the prototype: not the start or the end.
                if (key && pickable.has(key)) {
                  setStep(key);
                  setDraft('');
                }
              }}
            />
          </div>
          <aside className="flex flex-col gap-3" aria-label="Your comments">
            <PartLabel className="mb-0">
              Your comments{' '}
              <span className="text-meta font-normal normal-case tracking-normal">
                {comments.length}
              </span>
            </PartLabel>
            {comments.length === 0 ? (
              <div className="text-meta text-ink-500">None yet.</div>
            ) : (
              <ul className="flex flex-col gap-2">
                {comments.map((c, i) => (
                  <li
                    key={i}
                    className="rounded-[6px] border border-ink-100 bg-paper-50 px-2.5 py-2"
                  >
                    <div className="text-meta text-ink-500">{stepName(c.step)}</div>
                    <div className="whitespace-pre-wrap text-body-sm text-ink-900">{c.text}</div>
                    <button
                      type="button"
                      className="text-meta font-medium text-ink-700 underline underline-offset-[3px] hover:text-ink-900"
                      onClick={() => setComments((prev) => prev.filter((_, j) => j !== i))}
                      aria-label={`Remove the comment on ${stepName(c.step)}`}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <hr className="border-ink-100" />
            {step ? (
              <div>
                <label
                  htmlFor="cm-text"
                  className="mb-1.5 block text-meta font-medium text-ink-700"
                >
                  Comment on “{stepName(step)}”
                </label>
                <textarea
                  id="cm-text"
                  rows={3}
                  maxLength={MAX_COMMENT}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="What is different, missing or wrong?"
                  className="block w-full resize-y rounded-card border border-ink-200 bg-paper-0 px-3 py-2 text-body leading-5 text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600"
                />
                <div className="mt-2 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    className="text-meta font-medium text-ink-700 underline underline-offset-[3px] hover:text-ink-900"
                    onClick={() => setStep(null)}
                  >
                    Cancel
                  </button>
                  <WorkspaceButton disabled={!draft.trim()} onClick={addComment}>
                    Add comment
                  </WorkspaceButton>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <div className="text-meta text-ink-500">
                  Pick a step on the map to comment on it, or choose it here.
                </div>
                <WorkspaceSelect
                  aria-label="Step to comment on"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) setStep(e.target.value);
                  }}
                >
                  <option value="">Choose a step…</option>
                  {steps.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.name}
                    </option>
                  ))}
                </WorkspaceSelect>
              </div>
            )}
          </aside>
        </div>
      </ClientCard>

      <ClientCard>
        <label htmlFor="cm-name" className="mb-1.5 block text-meta font-medium text-ink-700">
          Type your full name
        </label>
        <WorkspaceInput
          id="cm-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={who.full_name}
          autoComplete="off"
          aria-describedby="cm-name-help"
          className="max-w-[360px]"
        />
        <div
          id="cm-name-help"
          className={cn(
            'mt-1.5 text-meta',
            name.trim() && !nameOk ? 'text-posture-critical' : 'text-ink-500',
          )}
        >
          {name.trim() && !nameOk
            ? `Type your name as it appears above: ${who.full_name}`
            : `Confirming as ${nameAndTitle(who.full_name, who.title)}. This link was sent only to you and shows only this map.`}
        </div>
        {problem || tooLong ? (
          <div role="alert" className="mt-3 text-meta text-posture-critical">
            {tooLong
              ? 'Your comments are too long to send together. Shorten some of them.'
              : problem}
          </div>
        ) : null}
        <div className="mt-4 flex flex-wrap justify-between gap-2">
          <WorkspaceButton
            size="md"
            disabled={comments.length === 0 || busy !== null || tooLong}
            title={comments.length === 0 ? 'Add a comment on a step first' : undefined}
            onClick={() => void finish('send')}
          >
            {busy === 'send' ? 'Sending…' : 'Send my comments'}
          </WorkspaceButton>
          <WorkspaceButton
            size="md"
            variant="primary"
            disabled={!nameOk || busy !== null || tooLong}
            title={nameOk ? undefined : 'Type your name first'}
            onClick={() => void finish('validate')}
          >
            {busy === 'validate' ? 'Sending…' : 'This is how we work'}
          </WorkspaceButton>
        </div>
      </ClientCard>
    </>
  );
}
